import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart } from './ShoppingCart';
import { products, generateLengthOptions } from './productData';
import { useShopContext } from './ShopContext';

// Import fallback image
import defaultImage from '../../assets/nt_white_logo.png';

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const shopContext = useShopContext();
  const [product, setProduct] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedWidth, setSelectedWidth] = useState(null);
  const [selectedLength, setSelectedLength] = useState(null);
  const [lengthOptions, setLengthOptions] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [optionDescription, setOptionDescription] = useState('');
  
  // Find product
  useEffect(() => {
    const foundProduct = products.find(p => p.id === productId);
    if (foundProduct) {
      setProduct(foundProduct);
      
      // Set default image
      setCurrentImage(foundProduct.defaultImage || foundProduct.image);
      
      // Set default options
      if (foundProduct.options && foundProduct.options.length > 0) {
        const defaultOption = foundProduct.options[0];
        setSelectedOption(defaultOption);
        setOptionDescription(defaultOption.description || '');
        
        // Set option image if available
        if (defaultOption.image) {
          setCurrentImage(defaultOption.image);
        }
      }
      
      if (foundProduct.widthOptions && foundProduct.widthOptions.length > 0) {
        setSelectedWidth(foundProduct.widthOptions[0]);
        
        // Generate appropriate length options based on selected width and product settings
        const maxLength = foundProduct.maxLength || foundProduct.widthOptions[0].maxLength || 30;
        const minLength = foundProduct.minLength || foundProduct.widthOptions[0].minLength || 1;
        
        setLengthOptions(generateLengthOptions(maxLength, minLength));
        
        // Default to minLength
        setSelectedLength({ value: minLength.toString(), label: `${minLength}m`, price: minLength });
      }
    } else {
      // Product not found, redirect to shop
      navigate('/shop');
    }
    
    window.scrollTo(0, 0);
  }, [productId, navigate]);

  // Update length options when width changes
  useEffect(() => {
    if (selectedWidth && product) {
      const maxLength = product.maxLength || selectedWidth.maxLength || 30;
      const minLength = product.minLength || selectedWidth.minLength || 1;
      
      setLengthOptions(generateLengthOptions(maxLength, minLength));
      
      // Reset length selection to minLength if the current selection is outside bounds
      if (!selectedLength || parseInt(selectedLength.value) < minLength || parseInt(selectedLength.value) > maxLength) {
        setSelectedLength({ value: minLength.toString(), label: `${minLength}m`, price: minLength });
      }
    }
  }, [selectedWidth, product, selectedLength]);

  // Handle option selection
  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    
    // Update option description if available
    if (option.description) {
      setOptionDescription(option.description);
    }
    
    // Update product image if the option has an image
    if (option.image) {
      setCurrentImage(option.image);
    } else if (product) {
      // Fallback to default product image
      setCurrentImage(product.defaultImage || product.image);
    }
  };

  if (!product) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center" style={{ backgroundColor: 'oklch(27.8% 0.033 256.848)' }}>
        <div className="text-white">Loading product...</div>
      </div>
    );
  }

  const handleAddToCart = () => {
    // Validate that required options are selected
    if (!selectedOption) {
      setValidationError("Lūdzu, izvēlieties tonējuma opciju");
      return;
    }
    
    if (product.widthOptions && product.widthOptions.length > 0 && !selectedWidth) {
      setValidationError("Lūdzu, izvēlieties platumu");
      return;
    }
    
    if (lengthOptions.length > 0 && !selectedLength) {
      setValidationError("Lūdzu, izvēlieties garumu");
      return;
    }
    
    // Clear any existing validation errors
    setValidationError(null);
    
    // Calculate price based on all selections
    const basePrice = product.discountPrice || product.price;
    
    // Calculate total price
    let finalPrice;
    if (selectedWidth && selectedLength) {
      // Get the width price from the selected width option
      const widthPrice = selectedWidth.price || basePrice;
      
      // Get the length value (as a number)
      const length = parseInt(selectedLength.value);
      
      // Calculate final price as width price * length
      finalPrice = widthPrice * length;
      
      // Add option price if applicable
      if (selectedOption && selectedOption.price) {
        finalPrice += selectedOption.price * length;
      }
    } else {
      finalPrice = basePrice;
    }
    
    // Create product with all option details
    const productToAdd = {
      ...product,
      selectedOption: selectedOption?.name,
      selectedWidth: selectedWidth?.value,
      selectedLength: selectedLength?.value,
      selectedImage: currentImage,
      finalPrice: finalPrice
    };
    
    // Only call addToCart if shopContext exists and has the method
    if (shopContext && typeof shopContext.addToCart === 'function') {
      shopContext.addToCart(productToAdd, quantity);
      
      // Show success message
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 3000);
      
      setIsCartOpen(true);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const toggleCart = () => {
    setIsCartOpen(!isCartOpen);
  };

  const handleQuantityChange = (newValue) => {
    // Ensure quantity is a valid number between 1 and stock
    const maxStock = product.stock || 99;
    
    if (newValue < 1) {
      setQuantity(1);
    } else if (newValue > maxStock) {
      setQuantity(maxStock);
    } else {
      setQuantity(newValue);
    }
  };

  // Calculate final price
  const basePrice = product.discountPrice || product.price;
  
  // Calculate total price
  let finalPrice;
  if (selectedWidth && selectedLength) {
    // Get the width price from the selected width option
    const widthPrice = selectedWidth.price || basePrice;
    
    // Get the length value (as a number)
    const length = parseInt(selectedLength.value);
    
    // Calculate final price as width price * length
    finalPrice = widthPrice * length;
    
    // Add option price if applicable
    if (selectedOption && selectedOption.price) {
      finalPrice += selectedOption.price * length;
    }
  } else {
    finalPrice = basePrice;
  }

  return (
    <div className="pt-20 min-h-screen" style={{ backgroundColor: 'oklch(27.8% 0.033 256.848)' }}>
      {/* Success Message */}
      {addedToCart && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg">
          {quantity} {quantity > 1 ? 'vienību' : 'vienība'} pievienota grozam
        </div>
      )}
      
      {/* Validation Error Message */}
      {validationError && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-md shadow-lg">
          {validationError === "Please select a tint option" ? "Lūdzu, izvēlieties tonējuma opciju" :
           validationError === "Please select a width" ? "Lūdzu, izvēlieties platumu" :
           validationError === "Please select a length" ? "Lūdzu, izvēlieties garumu" :
           validationError}
        </div>
      )}
      
      {/* Back Button */}
      <div className="container mx-auto px-4 py-4">
        <button 
          onClick={handleGoBack}
          className="flex items-center text-gray-300 hover:text-white"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          <span>Atpakaļ uz produktiem</span>
        </button>
      </div>
      
      {/* Cart Icon */}
      <div className="fixed right-4 top-24 z-50">
        <button 
          onClick={toggleCart}
          className="bg-white p-3 rounded-full shadow-lg hover:bg-gray-100 transition-all"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </button>
      </div>
      
      {/* Shopping Cart Sidebar */}
      <ShoppingCart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      
      <div className="container mx-auto px-4 py-8">
        <div className="bg-gray-900 rounded-lg overflow-hidden shadow-xl">
          <div className="md:flex">
            {/* Product Image */}
            <div className="md:w-1/2 bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-center p-4 md:p-8">
              <img 
                src={currentImage || product.defaultImage || product.image || defaultImage} 
                alt={product.name} 
                className="w-full max-w-md max-h-[400px] object-contain mx-auto"
                onError={(e) => {
                  console.error("Image failed to load:", currentImage);
                  e.target.onerror = null;
                  e.target.src = defaultImage;
                }}
              />
            </div>
            
            {/* Product Info */}
            <div className="md:w-1/2 p-4 md:p-8">
              <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2 mb-2">
                <h2 className="text-3xl font-bold text-white mb-4">{product.name}</h2>
                
                {/* Rating */}
                <div className="flex items-center bg-gray-800 px-3 py-1 rounded self-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-400 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-white">{product.rating}</span>
                </div>
              </div>
              
              {/* Price */}
              <div className="flex flex-wrap items-center mt-4 mb-6 gap-2">
                {product.discountPrice ? (
                  <>
                    <span className="text-gray-400 line-through mr-3 text-xl">€{product.price.toFixed(2)}</span>
                    <span className="text-green-400 font-bold text-2xl">€{finalPrice.toFixed(2)}</span>
                  </>
                ) : (
                  <span className="text-white font-bold text-2xl">€{finalPrice.toFixed(2)}</span>
                )}
                
                {product.priceUnit && (
                  <span className="text-gray-400 ml-2">/{product.priceUnit}</span>
                )}
                
                {/* Availability */}
                <div className="ml-auto mt-2 md:mt-0">
                  {product.stock > 0 ? (
                    <span className="bg-green-900 text-green-300 px-3 py-1 rounded text-sm">
                      Pieejams
                    </span>
                  ) : (
                    <span className="bg-red-900 text-red-300 px-3 py-1 rounded text-sm">
                      Nav pieejams
                    </span>
                  )}
                </div>
              </div>
              
              {/* Description */}
              <p className="text-gray-300 mb-6">
                {product.description}
              </p>
              
              {/* Option-specific description */}
              {optionDescription && (
                <div className="mb-6 p-3 bg-gray-800 rounded-md">
                  <p className="text-gray-300 text-sm">
                    {optionDescription}
                  </p>
                </div>
              )}
              
              {/* Tint Type Options */}
              {product.options && product.options.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-white font-semibold mb-3">Tonējuma veids*</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleOptionSelect(option)}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          selectedOption === option
                            ? 'bg-green-400 text-black'
                            : 'bg-gray-700 text-white hover:bg-gray-600'
                        }`}
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Width Options */}
              {product.widthOptions && product.widthOptions.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-white font-semibold mb-3">Platums*</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.widthOptions.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedWidth(option)}
                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          selectedWidth === option
                            ? 'bg-green-400 text-black'
                            : 'bg-gray-700 text-white hover:bg-gray-600'
                        }`}
                      >
                        {option.value}m
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Length Options */}
              {lengthOptions.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-white font-semibold mb-3">Garums*</h3>
                  <div className="w-full">
                    <select 
                      className="w-full bg-gray-800 text-white border border-gray-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
                      value={selectedLength?.value || ""}
                      onChange={(e) => {
                        const selected = lengthOptions.find(option => option.value === e.target.value);
                        if (selected) setSelectedLength(selected);
                      }}
                    >
                      <option value="" disabled>Izvēlieties garumu (metri)</option>
                      {lengthOptions.map((option, index) => (
                        <option key={index} value={option.value}>
                          {option.value}m
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              
              {/* Quantity */}
              <div className="mb-6">
                <h3 className="text-white font-semibold mb-3">Daudzums</h3>
                <div className="flex items-center">
                  <button 
                    onClick={() => handleQuantityChange(quantity - 1)}
                    className="bg-gray-700 text-white w-8 h-8 flex items-center justify-center rounded-l-md hover:bg-gray-600"
                  >
                    -
                  </button>
                  <div
                    className="w-12 bg-gray-800 border-0 text-center text-white h-8 flex items-center justify-center"
                  >
                    {quantity}
                  </div>
                  <button 
                    onClick={() => handleQuantityChange(quantity + 1)}
                    className="bg-gray-700 text-white w-8 h-8 flex items-center justify-center rounded-r-md hover:bg-gray-600"
                  >
                    +
                  </button>
                </div>
              </div>
              
              {/* Add to Cart Button */}
              <button 
                onClick={handleAddToCart}
                disabled={product.stock <= 0}
                className={`flex items-center justify-center w-full md:w-auto px-6 py-3 rounded-md font-medium shadow-md ${
                  product.stock > 0 
                    ? 'bg-green-400 hover:bg-green-500 active:bg-green-600 text-black transition-all duration-200 transform hover:scale-[1.03] active:scale-[0.98] hover:shadow-lg' 
                    : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                </svg>
                Pievienot grozam
              </button>
              
              {/* Price Breakdown for all products with dimensions */}
              {selectedWidth && selectedLength && (
                <div className="mt-4 px-4 py-3 bg-gray-800 rounded-md">
                  <h3 className="text-white font-semibold mb-2">Cenas aprēķins</h3>
                  <div className="text-sm text-gray-300">
                    <p>Platuma cena: €{(selectedWidth.price || basePrice).toFixed(2)}</p>
                    <p>Garums: {selectedLength.value}m</p>
                    {selectedOption && selectedOption.price > 0 && (
                      <p>Papildus par opciju: €{selectedOption.price.toFixed(2)}/m</p>
                    )}
                    <p>Aprēķins: €{(selectedWidth.price || basePrice).toFixed(2)} × {selectedLength.value}m {selectedOption && selectedOption.price > 0 ? `+ €${selectedOption.price.toFixed(2)} × ${selectedLength.value}m` : ''}</p>
                    <p className="font-semibold text-white mt-1">Kopā: €{finalPrice.toFixed(2)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Tabs Section */}
          <div className="p-4 md:p-8 border-t border-gray-800">
            <div className="py-2">
              <ul className="space-y-2">
                {product.features && product.features.map((feature, index) => (
                  <li key={index} className="flex items-start text-gray-300">
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail; 